// The §4.12 stock adjustment's rules, pure (spec 06 §4.12, D-315).
//
// A manual adjustment is §5 row 25's `adjustment` on POST /movements, or row
// 48's explicit hold on POST /adjustment-requests. Both take a bulk quantity of
// one product (and its batch) between two locations of ONE plant. The ledger's
// own convention for the side a correction is booked against (§3.4 step 7,
// §3.5, §3.2) is what the form names in words:
//
//   * an INCREASE posts from a counter location to the stock location — the
//     plant's `supplier` location ("not on the books, found") or its
//     `production` location (work in progress: the copper a crew carried back
//     after the order closed, D-308);
//   * a DECREASE posts from the stock location to a counter location — the
//     plant's `scrap` location (written off) or its `production` location.
//
// Three rules here are the FORM's and the server does not hold them (the spec
// says so): the reasons follow the direction, a tracked product needs a batch,
// and an increase out of work in progress is limited to that batch's work in
// progress. The server re-validates everything else and stays the boundary.
//
// No quantity is ever a JS number here: every comparison runs on the ×1e6
// scaled integers `parseMeasure` returns (D-042).

import { normalizeTypedDecimal } from "../../sales/lib/money";
import { parseMeasure } from "./measure";

export type AdjustDirection = "increase" | "decrease";
export type AdjustCounter = "found" | "wip" | "scrap";

export const ADJUST_DIRECTIONS: readonly AdjustDirection[] = ["increase", "decrease"];

/** The plant location each counter names, by §2.1 kind. Each plant holds
 * exactly one of each (provisioned with the facility). */
export const COUNTER_KIND: Readonly<Record<AdjustCounter, "supplier" | "production" | "scrap">> = {
  found: "supplier",
  wip: "production",
  scrap: "scrap",
};

/**
 * What an increase and a decrease may be booked against, first = default. A
 * truck (`in_transit`) is written off to scrap and nothing else: §9.10's
 * resolution is `in_transit → scrap`, and booking a lost load into the origin
 * plant's work in progress would put it on the line with no order.
 */
export function countersFor(
  direction: AdjustDirection,
  placeKind?: string,
): readonly AdjustCounter[] {
  if (placeKind === "in_transit") return ["scrap"];
  return direction === "increase" ? ["found", "wip"] : ["scrap", "wip"];
}

/** §9.10: a truck's write-off goes "through the approval chain" whatever its
 * value, so the form sends it to POST /adjustment-requests (§5 row 48). */
export function holdRequired(placeKind: string | undefined): boolean {
  return placeKind === "in_transit";
}

/** What an entry on a place adds to its link: a truck can only be written
 * off, so its entry opens the form as a decrease. Every other place leaves
 * the direction to the reader. */
export function placeEntrySearch(
  kind: string | undefined,
): { readonly direction: "decrease" } | Record<string, never> {
  return kind === "in_transit" ? { direction: "decrease" } : {};
}

/**
 * The D-211 fence on a decrease booked against work in progress. Such a
 * decrease moves stock INTO the production location, which is what an issue
 * does, and module 06 refuses an issue of stock quality has not released. The
 * manual door does not hold that rule (D-211 leaves `adjustment` open because
 * it records counted truth), so the form holds it: stock in quarantine and a
 * batch that is not released cannot be booked into work in progress. They can
 * still be written off to scrap.
 */
export type WipFence = "quarantine_place" | "lot_not_released";

export function wipFence(input: {
  readonly direction: AdjustDirection;
  readonly counter: AdjustCounter;
  readonly placeKind: string | undefined;
  readonly lotQcStatus: string | undefined;
}): WipFence | undefined {
  if (input.direction !== "decrease" || input.counter !== "wip") return undefined;
  if (input.placeKind === "quarantine" || input.placeKind === "in_transit") {
    return "quarantine_place";
  }
  if (input.lotQcStatus !== undefined && input.lotQcStatus !== "released") {
    return "lot_not_released";
  }
  return undefined;
}

/**
 * Whether the work-in-progress limit binds. A component an order drew holds
 * work in progress at or above zero, and an increase out of it may not take
 * more than is there. A batch whose work in progress is BELOW zero is an
 * order's output: `production_output` posts `production → stock`, so the
 * finished product's level there is minus what was receipted, and an increase
 * against it is a further receipt (spec 04 S7's R1 notice). The limit does not
 * apply there, or the R1 link could never record.
 */
export function wipLimitApplies(wipQty: string | undefined): boolean {
  const scaled = parseMeasure(wipQty);
  return scaled === undefined || scaled >= 0n;
}

/**
 * Where a container's leftover goes (D-308 round 2, `WoSupport.returnContainer`).
 *   * `no_stock` — `empty`, `consumed`, `retired`, `shipped`: the container
 *     holds nothing a return could go back onto, so the leftover is loose
 *     stock at the batch, which is what this form records.
 *   * `live` — `filled`, `in_use`: the metres go back onto the container,
 *     which is its card's Re-measure. A loose adjustment beside it would be
 *     counted a second time at the next re-measure.
 *   * `held` — `quarantined`, `in_transit` (and any status a later release
 *     adds): quality decides first; loose stock would be free stock without
 *     that decision.
 */
export type ContainerRule = "no_stock" | "live" | "held";

const CONTAINER_HOLDS_NO_STOCK: ReadonlySet<string> = new Set([
  "empty",
  "consumed",
  "retired",
  "shipped",
]);
const CONTAINER_TAKES_METRES: ReadonlySet<string> = new Set(["filled", "in_use"]);

export function containerRule(status: string): ContainerRule {
  if (CONTAINER_HOLDS_NO_STOCK.has(status)) return "no_stock";
  if (CONTAINER_TAKES_METRES.has(status)) return "live";
  return "held";
}

/** A serial or measured-unit product lives in containers. An increase of it
 * as loose stock is legitimate (an emptied drum's leftover, D-308 round 2),
 * but the person says so in a tick, because the same material still on a drum
 * belongs to that drum's Re-measure. */
export function looseAttestationRequired(
  direction: AdjustDirection,
  tracking: string | undefined,
): boolean {
  return direction === "increase" && (tracking === "serial" || tracking === "measured_unit");
}

/** A floor `scan_code` names a container by its label URL (`…/q/su/{id}`) or
 * by its serial (spec 04 `resolveMaterialScan`). The link carries which. */
export function unitRefOfScanCode(
  code: string,
): { readonly unit_id: string } | { readonly unit_serial: string } | Record<string, never> {
  const match =
    /\/q\/su\/([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})\s*$/iu.exec(code);
  if (match?.[1] !== undefined) return { unit_id: match[1].toLowerCase() };
  const serial = code.trim();
  return serial.length > 0 ? { unit_serial: serial } : {};
}

/**
 * The reasons a person may give, by direction. The nine-value §2.4 vocabulary
 * is closed; three of its values belong to flows that post them themselves
 * (`count_variance` the count, `remeasure` the re-measure, `cut_tolerance` the
 * cut) and are never offered here. `found` names a gain and the four losses
 * name a loss, so a gain booked as "damage" cannot be typed.
 */
export const INCREASE_REASONS = ["found", "data_correction"] as const;
export const DECREASE_REASONS = [
  "damage",
  "loss",
  "shrinkage",
  "qc_fail",
  "data_correction",
] as const;
export type AdjustReason = (typeof INCREASE_REASONS)[number] | (typeof DECREASE_REASONS)[number];

export function reasonsFor(direction: AdjustDirection): readonly AdjustReason[] {
  return direction === "increase" ? INCREASE_REASONS : DECREASE_REASONS;
}

/** The kinds a person may name as "where the stock is". Physical stock only:
 * the counter locations are chosen by the counter select, never here. A
 * decrease may also write off what a truck lost (§9.10, `in_transit`). */
const STOCK_KINDS_BASE = [
  "bin",
  "zone",
  "warehouse",
  "receiving",
  "shipping",
  "quarantine",
] as const;

export function stockKindsFor(direction: AdjustDirection): readonly string[] {
  return direction === "decrease" ? [...STOCK_KINDS_BASE, "in_transit"] : STOCK_KINDS_BASE;
}

/** Every kind that physically holds stock outside work in progress. The held
 * payload's direction is read from which endpoint is one of these. */
export const PHYSICAL_KINDS: ReadonlySet<string> = new Set([...STOCK_KINDS_BASE, "in_transit"]);

/** A place a stock adjustment may name as "where the stock is" — the entries
 * on §4.1, §4.3 and §4.6 show only on these. Work in progress, found, scrap,
 * customer and supplier are what the form books AGAINST, never the stock side. */
export function isAdjustablePlace(kind: string | undefined): boolean {
  return kind !== undefined && PHYSICAL_KINDS.has(kind);
}

/** The contract's note bounds (`MovementCreateRequest.note`, D-225). */
export const NOTE_MIN = 10;
export const NOTE_MAX = 2000;

export function noteLength(note: string): number {
  return note.trim().length;
}

export function noteOk(note: string): boolean {
  const n = noteLength(note);
  return n >= NOTE_MIN && n <= NOTE_MAX;
}

/** A tracked product (lot, serial or measured unit) carries its identity in
 * the batch: a batch-less gain would mint a second, anonymous identity for
 * material that already has one. The form's rule; the server accepts it. */
export function lotRequired(tracking: string | undefined): boolean {
  return tracking !== undefined && tracking !== "none";
}

/** The contract's `InventoryPositiveMeasure`. */
const POSITIVE_MEASURE = /^(?=.*[1-9])\d{1,12}(\.\d{1,6})?$/u;

/** A quantity already in the wire's form: a link never carries a comma. */
export function isWireQuantity(text: string): boolean {
  return POSITIVE_MEASURE.test(text);
}

export type QuantityCheck =
  | { readonly ok: true; readonly value: string }
  | { readonly ok: false; readonly reason: "empty" | "invalid" };

/**
 * The typed quantity, in the wire's form. A ru keyboard types `12,5`; the
 * reader's own separators are normalised first (the sales price box's rule,
 * #204), then the contract's pattern decides: above zero, at most 12 whole
 * digits and 6 decimals.
 */
export function checkQuantity(text: string, locale: string): QuantityCheck {
  if (text.trim().length === 0) return { ok: false, reason: "empty" };
  const normalised = normalizeTypedDecimal(text, locale);
  return POSITIVE_MEASURE.test(normalised)
    ? { ok: true, value: normalised }
    : { ok: false, reason: "invalid" };
}

/** True when `quantity` is strictly more than `limit` (both exact decimals).
 * An unreadable side answers false: the form does not refuse on a number it
 * cannot read, and the server decides. */
export function exceeds(quantity: string, limit: string | undefined): boolean {
  if (limit === undefined) return false;
  const q = parseMeasure(quantity);
  const l = parseMeasure(limit);
  if (q === undefined || l === undefined) return false;
  return q > l;
}

/** The two endpoints of the movement the form posts. */
export function endpointsOf(
  direction: AdjustDirection,
  stockLocationId: string,
  counterLocationId: string,
): { from_location_id: string; to_location_id: string } {
  return direction === "increase"
    ? { from_location_id: counterLocationId, to_location_id: stockLocationId }
    : { from_location_id: stockLocationId, to_location_id: counterLocationId };
}

export interface AdjustmentDraft {
  readonly direction: AdjustDirection;
  readonly productId: string;
  readonly lotId: string;
  readonly stockLocationId: string;
  readonly counterLocationId: string;
  readonly quantity: string;
  readonly uom: string;
  readonly reason: AdjustReason;
  readonly note: string;
  /** The work order the adjustment is recorded against (spec 06 §3.9, D-391 ruling 3). */
  readonly workOrderId?: string | undefined;
}

export interface AdjustmentBody {
  movement_type: "adjustment";
  product_id: string;
  lot_id?: string;
  from_location_id: string;
  to_location_id: string;
  quantity: string;
  uom: string;
  reason_code: AdjustReason;
  note: string;
  work_order_id?: string;
}

/** The exact §5 row 25 / row 48 body. The note is sent trimmed, as the
 * contract counts it. */
export function adjustmentBody(draft: AdjustmentDraft): AdjustmentBody {
  return {
    movement_type: "adjustment",
    product_id: draft.productId,
    ...(draft.lotId.length > 0 ? { lot_id: draft.lotId } : {}),
    ...endpointsOf(draft.direction, draft.stockLocationId, draft.counterLocationId),
    quantity: draft.quantity,
    uom: draft.uom,
    reason_code: draft.reason,
    note: draft.note.trim(),
    ...(draft.workOrderId !== undefined && draft.workOrderId.length > 0
      ? { work_order_id: draft.workOrderId }
      : {}),
  };
}

/** The held payload's direction, from which endpoint is physical stock. Work
 * in progress, found, scrap and the rest are the counter side. Two physical
 * endpoints is a relocation the server accepts as an "adjustment"; neither is
 * a shape this screen did not post, and both are named rather than guessed. */
export function directionOfKinds(
  fromKind: string | undefined,
  toKind: string | undefined,
): AdjustDirection | "relocation" | undefined {
  if (fromKind === undefined || toKind === undefined) return undefined;
  const fromPhysical = PHYSICAL_KINDS.has(fromKind);
  const toPhysical = PHYSICAL_KINDS.has(toKind);
  if (toPhysical && !fromPhysical) return "increase";
  if (fromPhysical && !toPhysical) return "decrease";
  if (fromPhysical && toPhysical) return "relocation";
  return undefined;
}

/** The counter a location kind stands for, or undefined for any other kind. */
export function counterOfKind(kind: string | undefined): AdjustCounter | undefined {
  if (kind === "supplier") return "found";
  if (kind === "production") return "wip";
  if (kind === "scrap") return "scrap";
  return undefined;
}

/** A held payload, read defensively: `movement_payload` is `unknown` jsonb on
 * the wire (§2.12). Every field is optional and a missing one stays missing. */
export interface HeldPayload {
  readonly movementType?: string | undefined;
  readonly productId?: string | undefined;
  readonly lotId?: string | undefined;
  readonly fromLocationId?: string | undefined;
  readonly toLocationId?: string | undefined;
  readonly quantity?: string | undefined;
  readonly uom?: string | undefined;
  readonly reason?: string | undefined;
  readonly note?: string | undefined;
}

function field(record: Record<string, unknown>, key: string): string | undefined {
  const value = record[key];
  return typeof value === "string" && value.length > 0 ? value : undefined;
}

export function readHeldPayload(payload: unknown): HeldPayload | undefined {
  if (typeof payload !== "object" || payload === null || Array.isArray(payload)) return undefined;
  const record = payload as Record<string, unknown>;
  return {
    movementType: field(record, "movement_type"),
    productId: field(record, "product_id"),
    lotId: field(record, "lot_id"),
    fromLocationId: field(record, "from_location_id"),
    toLocationId: field(record, "to_location_id"),
    quantity: field(record, "quantity"),
    uom: field(record, "uom"),
    reason: field(record, "reason_code"),
    note: field(record, "note"),
  };
}

function isReason(direction: AdjustDirection, value: string | undefined): value is AdjustReason {
  return value !== undefined && (reasonsFor(direction) as readonly string[]).includes(value);
}

/** Narrow a reason to the ones a direction offers; anything else is dropped. */
export function reasonForDirection(
  direction: AdjustDirection,
  value: string | undefined,
): AdjustReason | undefined {
  return isReason(direction, value) ? value : undefined;
}

/** Narrow a counter to the ones a direction offers; anything else is dropped. */
export function counterForDirection(
  direction: AdjustDirection,
  value: string | undefined,
): AdjustCounter | undefined {
  return countersFor(direction).find((counter) => counter === value);
}
